import React, { useEffect, useState } from "react";
import {
  createDoor,
  fetchSlabStyles,
  slabStylesForCore,
  getSlabStyleImage,
} from "./utilities";
import DoorSpecifications from "./Components/DoorSpecifications";
import { Tooltip } from "@mui/material";

const DoorBuilder = (props) => {
  const [pricing, setPricing] = useState(0);
  const [isDoubleDoor, setIsDoubleDoor] = useState();
  const [widthOptions, setWidthOptions] = useState([]);
  const [slabStyles, setSlabStyles] = useState([]);
  const [defaultMargin, setDefaultMargin] = useState(props.formData.margin);
  const [marginFlag, setMarginFlag] = useState(false);
  const [formValid, setFormValid] = useState(false);

  const [data, setData] = useState({
    casing_type: props.formData.casing_type || "",
    core: props.formData.core || "",
    hinge_finish: props.formData.hinge_finish || "",
    size_jamb_type: props.formData.size_jamb_type || "",
    stop: props.formData.stop || "",
    handing: props.formData.handing,
    door_type: props.formData.door_type,
    stop_applied: props.formData.stop_applied || "",
    applied: props.formData.applied || "",
    bolt_type: props.formData.bolt_type || "",
    backset: props.formData.backset || "",
    isDouble: props.formData.door_type === "double" || 0,
    height: `6'8"`,
    qty: 1,
    margin: props.formData.margin,
    estimated_price: props.formData.estimated_price || 0,
    ftwayne_prep: props.formData.ftwayne_prep || false,
  });

  const updateStates = [
    props.formData.casing_type,
    props.formData.hinge_finish,
    props.formData.size_jamb_type,
    props.formData.stop,
    props.formData.core,
    props.formData.applied,
    props.formData.stop_applied,
    props.formData.slab_style,
    props.formData.door_type,
    props.formData.width,
    props.formData.catch_astragal,
    props.formData.bolt_type,
    props.formData.backset,
    props.formData.ftwayne_prep,
    data.margin,
    data.qty,
    pricing,
  ];

  const [requiredFields, setRequiredFields] = useState([]);

  useEffect(() => {
    const defaultFields = [
      "size_jamb_type",
      "core",
      "casing_type",
      "bolt_type",
      "backset",
      "handing",
      "slab_style",
      "width",
    ];

    const bifoldFields = ["core", "width", "slab_style", "handing"]; // Example: Adjust fields specific to Bifold

    setRequiredFields(
      props.formData.door_type === "bifold" ? bifoldFields : defaultFields
    );
  }, [props.formData.door_type, data]);

  const checkRequiredFields = () => {
    const missingFields = requiredFields.filter((field) => {
      const value = data[field]; // Get field value

      return (
        value === undefined || value === null || value.toString().trim() === ""
      );
    });

    if (missingFields.length === 0) {
      setFormValid(true);
      return null; // No missing fields, return nothing
    } else {
      return "Please fill out all fields: " + missingFields.join(", ");
    }
  };

  useEffect(() => {
    const missingFields = requiredFields.filter((field) => {
      const value = data[field];
      return (
        value === undefined || value === null || value.toString().trim() === ""
      );
    });

    setFormValid(missingFields.length === 0);
  }, [data, requiredFields]);

  // Change the casing type whenever formData changes but keep old data as well
  useEffect(() => {
    //This makes stops impossible to select if Jamb is split
    if (
      props &&
      props.size_jamb_type &&
      props.size_jamb_type.includes("Door Slab Only")
    ) {
      setData({
        ...data,
        casing_type: null,
        hinge_finish: null,
        stop: null,
        stop_applied: null,
        applied: null,
        core: props.formData.core,
      });
    } else {
      setData({
        ...data,
        catch_astragal: props.formData.catch_astragal,
        door_type: props.formData.door_type,
        casing_type: props.formData.casing_type,
        hinge_finish: props.formData.hinge_finish,
        size_jamb_type: props.formData.size_jamb_type,
        stop: props.formData.stop,
        stop_applied: props.formData.stop_applied,
        applied: props.formData.applied,
        isDouble: props.formData.door_type === "double" || 0,
        core: props.formData.core,
        bolt_type: props.formData.bolt_type,
        backset: props.formData.backset,
        ftwayne_prep: props.formData.ftwayne_prep,
        estimated_price: isNaN(parseFloat(pricing)) ? 0.0 : parseFloat(pricing),
      });
    }
  }, updateStates);

  const handleDoorAdd = () => {
    //Add door to main Interior Door array
    var array = [...props.doors];
    var newDoor = createDoor(data);

    newDoor.isDouble = props.formData.door_type === "double" || 0;

    if (newDoor.isDouble) {
      newDoor.handing = "Double";
      newDoor.catch_astragal = props.formData.catch_astragal;
    } else {
      newDoor.catch_astragal = null;
    }

    newDoor.backset = props.formData.backset;
    newDoor.bolt_type = props.formData.bolt_type;
    newDoor.ftwayne_prep = props.formData.ftwayne_prep;

    array.push(newDoor);
    props.setDoors(array);

    //Set Default Data
    setData({
      height: `6'8"`,
      qty: 1,
      slab_style: newDoor.slab_style,
      isDouble: isDoubleDoor || 0,
      handing: isDoubleDoor ? "Double" : newDoor.handing,
      catch_astragal: props.formData.catch_astragal,
      casing_type: props.formData.casing_type,
      core: props.formData.core,
      size_jamb_type: props.formData.size_jamb_type,
      hinge_finish: props.formData.hinge_finish,
      stop: props.formData.stop,
      stop_applied: props.formData.stop_applied,
      applied: props.formData.applied,
      bolt_type: props.formData.bolt_type,
      backset: props.formData.backset,
      ftwayne_prep: props.formData.ftwayne_prep,
      margin: defaultMargin,
      estimated_price: isNaN(parseFloat(pricing)) ? 0.0 : parseFloat(pricing),
    });

    setMarginFlag(!marginFlag);
  };

  const handleChange = async (event) => {
    const { name, value, type, checked } = event.currentTarget;

    if (type === "checkbox") {
      await setData({ ...data, [name]: checked });
      setIsDoubleDoor(checked);
    } else if (name === "slab_style") {
      await setData({ ...data, [name]: value, width: null });
    } else if (name === "margin") {
      const newMargin = value; // Parse the value to an integer
      setDefaultMargin(newMargin); // Update the default margin state
      await setData({ ...data, [name]: newMargin }); // Update the margin in the data state
    } else {
      await setData({ ...data, [name]: value });
    }
  };

  const handleReset = () => {
    //reset door data to default
    setData({
      height: `6'8"`,
      qty: 1,
      casing_type: props.formData.casing_type,
      core: props.formData.core,
      size_jamb_type: props.formData.size_jamb_type,
      hinge_finish: props.formData.hinge_finish,
      stop: props.formData.stop,
      stop_applied: props.formData.stop_applied,
      applied: props.formData.applied,
      bolt_type: props.formData.bolt_type,
      backset: props.formData.backset,
      ftwayne_prep: props.formData.ftwayne_prep,
      isDouble: isDoubleDoor ? 1 : 0,
      handing: isDoubleDoor ? "Double" : null,
    });
  };

  const getWidthOptions = async (slab, core) => {
    try {
      const response = await fetch(
        `/api/doors/kits/getSlabOptions?slabStyle=${slab}&coreType=${core}`
      );
      const data = await response.json();

      if (data.message === "Internal Server Error") {
        await setWidthOptions([]);
        return;
      } else {
        await setWidthOptions(data.options);
        return;
      }
    } catch (error) {
      console.log(error);
      await setWidthOptions([]);
      return;
    }
  };

  useEffect(() => {
    if (data.slab_style !== undefined && data.core !== undefined) {
      getWidthOptions(data.slab_style, data.core);
    }
  }, [data.core, data.slab_style]);

  useEffect(() => {
    fetchSlabStyles().then(setSlabStyles);
  }, []);

  const showHanding =
    props.formData.door_type !== "double" &&
    props.formData.size_jamb_type !== "Door Slab Only(null)" &&
    props.formData.door_type !== "bifold";

  // Section numbers shift up when the handing step is hidden.
  const stepAfterHanding = showHanding ? 3 : 2;

  // Same control look as the Interior Door form this builder sits in.
  const selectCls =
    "app-input w-full rounded-xl border px-3 py-2.5 text-sm transition-colors";

  const inputCls =
    "app-input rounded-xl border px-3 py-2.5 text-sm transition-colors";

  const stepHeadingCls = "app-eyebrow mb-3";
  const labelCls = "mb-1 block text-sm font-medium";
  const optionTileCls = (selected) =>
    `flex cursor-pointer flex-col items-center rounded-xl border p-2.5 transition ${
      selected
        ? "border-sky-500 bg-sky-500/10 ring-2 ring-sky-500/30"
        : "app-surface hover:bg-[color:var(--app-hover)]"
    }`;

  return (
    <div className="app-card mt-5 overflow-hidden rounded-2xl border">
      <div className="flex flex-col lg:flex-row">
        {/* ── Left: Builder ── */}
        <div className="flex-1 min-w-0">
          {/* Step 1: Slab Style — everything below depends on it */}
          <div className="px-5 pb-4 pt-5 sm:px-6">
            <h2 className={stepHeadingCls}>
              1. Choose Slab Style
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {slabStylesForCore(slabStyles, data.core).map((style) => {
                const isSelected = data.slab_style === style.name;
                return (
                  <div
                    key={style.name}
                    role="button"
                    tabIndex={0}
                    aria-pressed={isSelected}
                    className={optionTileCls(isSelected)}
                    onClick={() =>
                      setData({ ...data, slab_style: style.name, width: null })
                    }
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        setData({ ...data, slab_style: style.name, width: null });
                      }
                    }}
                  >
                    <img
                      src={getSlabStyleImage(style.image)}
                      alt={style.name}
                      className="h-24 rounded-lg object-contain"
                    />
                    <span className={`mt-1.5 text-center text-xs font-semibold ${
                      isSelected ? "app-accent" : ""
                    }`}>
                      {style.name}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Step 2: Handing */}
          {showHanding && (
            <>
              <hr className="app-divider border-t" />
              <div className="px-5 py-5 sm:px-6">
                <h2 className={stepHeadingCls}>
                  2. Select Handing
                </h2>
                <div className="flex gap-4 justify-center">
                  {[
                    { id: "left", value: "Left Hand", img: "handing_left.jpg" },
                    { id: "right", value: "Right Hand", img: "handing_right.jpg" },
                  ].map((hand) => (
                    <label
                      key={hand.id}
                      htmlFor={`handing-${hand.id}`}
                      className={`${optionTileCls(
                        data.handing === hand.value
                      )} min-w-[8rem] !p-3`}
                    >
                      <img
                        src={require(`../../assets/DoorBuilder/${hand.img}`)}
                        alt={hand.value}
                        className="h-24 rounded-lg"
                      />
                      <span className={`mt-1.5 text-xs font-semibold ${
                        data.handing === hand.value ? "app-accent" : ""
                      }`}>
                        {hand.value}
                      </span>
                      <input
                        type="radio"
                        id={`handing-${hand.id}`}
                        name="handing"
                        value={hand.value}
                        checked={data.handing === hand.value}
                        onChange={handleChange}
                        required
                        className="hidden"
                      />
                    </label>
                  ))}
                </div>
              </div>
            </>
          )}

          <hr className="app-divider border-t" />

          {/* Step 3: Size & Quantity */}
          <div className="px-5 py-5 sm:px-6">
            <h2 className={stepHeadingCls}>
              {stepAfterHanding}. Size &amp; Quantity
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-12 gap-4 items-end">
              {/* Width */}
              <div className={props.formData.custom_margin ? "sm:col-span-6" : "sm:col-span-8"}>
                <label htmlFor="width" className={labelCls}>
                  Width
                </label>
                {data.slab_style && data.core ? (
                  <select
                    id="width"
                    name="width"
                    value={data.width || ""}
                    className={selectCls}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select width...</option>
                    {widthOptions.map((option) => {
                      if (option.productCode) {
                        let displayWidth = option.width;
                        if (data.isDouble) {
                          const [feet, inches] = option.width
                            .split("'")
                            .map((str) => parseInt(str));
                          const doubleTotalInches = (feet * 12 + inches) * 2;
                          const doubleFeet = Math.floor(doubleTotalInches / 12);
                          const doubleInches = doubleTotalInches % 12;
                          displayWidth = `${doubleFeet}'${doubleInches}"`;
                        }
                        return (
                          <option
                            value={`${option.width} - (${option.productCode})`}
                            key={option.width}
                          >
                            {option.width}{" "}
                            {data.isDouble
                              ? `(Actual Size: ${displayWidth})`
                              : ""}
                          </option>
                        );
                      } else {
                        return (
                          <option
                            disabled={true}
                            value={option.width}
                            key={option.width}
                          >
                            {option.width} - Non Stock (Call For Pricing)
                          </option>
                        );
                      }
                    })}
                  </select>
                ) : (
                  <div className="flex h-[42px] items-center rounded-xl border border-dashed border-amber-500/50 bg-amber-500/10 px-3">
                    <span className="text-xs font-medium text-amber-700 dark:text-amber-300">
                      Select slab style & core first
                    </span>
                  </div>
                )}
              </div>

              {/* QTY */}
              <div className="sm:col-span-4">
                <label htmlFor="door-builder-qty" className={labelCls}>
                  QTY
                </label>
                <input
                  id="door-builder-qty"
                  type="number"
                  name="qty"
                  className={inputCls + " w-full"}
                  value={data?.qty || 1}
                  min="1"
                  onChange={handleChange}
                />
              </div>

              {/* Margin (conditional) */}
              {props.formData.custom_margin && (
                <div className="sm:col-span-2">
                  <label htmlFor="door-builder-margin" className={labelCls}>
                    Margin (%)
                  </label>
                  <input
                    id="door-builder-margin"
                    type="text"
                    name="margin"
                    className={inputCls + " w-full"}
                    value={data?.margin || 30}
                    onChange={handleChange}
                    max={99}
                    onInput={(e) => {
                      let { value } = e.target;
                      value = value.replace(/[^0-9.]/g, "");
                      const parts = value.split(".");
                      if (parts.length > 2) { parts.pop(); value = parts.join("."); }
                      const integerPart = parts[0];
                      if (integerPart.length > 2) {
                        value = integerPart.substring(0, 2) + (parts[1] ? "." + parts[1] : "");
                      }
                      if (parts[1] && parts[1].length > 2) {
                        value = integerPart + "." + parts[1].substring(0, 2);
                      }
                      e.target.value = value;
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          <hr className="app-divider border-t" />

          {/* Step 4: Final Details */}
          <div className="px-5 py-5 sm:px-6">
            <h2 className={stepHeadingCls}>
              {stepAfterHanding + 1}. Final Details
            </h2>
            <div>
              <label
                htmlFor="door-builder-instructions"
                className={labelCls}
              >
                Special Instructions
              </label>
              <input
                id="door-builder-instructions"
                type="text"
                name="special_instructions"
                className={inputCls + " w-full"}
                value={data?.special_instructions || ""}
                onChange={handleChange}
                placeholder="Optional notes..."
              />
            </div>

            {/* Action buttons */}
            <div className="mt-5 flex flex-wrap justify-center gap-3 border-t app-divider pt-5">
              {formValid ? (
                <button
                  type="button"
                  onClick={handleDoorAdd}
                  className="app-btn bg-emerald-600 text-white shadow-sm hover:bg-emerald-700"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                  </svg>
                  Add to Quote
                </button>
              ) : (
                <Tooltip title={checkRequiredFields()}>
                  <button
                    type="button"
                    aria-disabled="true"
                    className="app-btn app-btn-secondary"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                    </svg>
                    Add to Quote
                  </button>
                </Tooltip>
              )}
              <button
                type="button"
                onClick={handleReset}
                className="app-btn app-btn-ghost"
              >
                Reset
              </button>
            </div>
          </div>
        </div>

        {/* ── Right: Specifications ── */}
        <div className="app-surface flex w-full flex-col border-t lg:w-80 lg:border-l lg:border-t-0 xl:w-96">
          {/* Specifications */}
          <div className="min-h-0 flex-1 px-4 pb-4 pt-5">
            <DoorSpecifications
              formData={props.formData}
              data={data}
              estimatedPrice={pricing}
              setEstimatedPrice={setPricing}
              setFormData={props.setFormData}
              marginFlag={marginFlag}
              formValid={formValid}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoorBuilder;
